import {
  Component,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AccountServiceProxy,
  BiositeStaffDataDto,
  ChangeUserPasswordInput,
  EditAdminUserDetailsInput,
  EditSupervisorUserDetailsInput,
  GetStaffUserDetailsForEditOutput,
  PasswordComplexitySetting,
  ProfileServiceProxy,
  SendEmailActivationLinkInput,
  StaffsAndUsersServiceProxy,
  StaffsServiceProxy,
  UpdateProjectAdministratorUserDetailsInput,
  UserServiceProxy,
} from '@shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';
import { EMPTY, Observable } from '@node_modules/rxjs';
import { finalize } from '@node_modules/rxjs/operators';
import { Router } from '@angular/router';

import { StaffUserLookupTableModalComponent } from '@app/components/user-details/lookup-table-modal/staff-user-lookup-table-modal.component';
import { PermissionCheckerService } from 'abp-ng2-module';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.css'],
})
export class UserDetailsComponent
  extends AppComponentBase
  implements OnInit, OnChanges {
  @ViewChild('staffUserLookupTableModal', { static: true })
  staffUserLookupTableModal: StaffUserLookupTableModalComponent;

  @Input() staff: GetStaffUserDetailsForEditOutput;
  @Input() ownDetailsEdit: Boolean;

  saving = false;
  user;
  userName = '';

  newPassword: null;
  passwordComplexitySetting: PasswordComplexitySetting = new PasswordComplexitySetting();
  signOffPerson;

  constructor(
    injector: Injector,
    private _accountServiceProxy: AccountServiceProxy,
    private _profileService: ProfileServiceProxy,
    private _userService: UserServiceProxy,
    private _router: Router,
    private _permissionChecker : PermissionCheckerService,
    private _staffsAndUsersServiceProxy: StaffsAndUsersServiceProxy,
    private _staffsServiceProxy: StaffsServiceProxy
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this._profileService.getPasswordComplexitySetting().subscribe((result) => {
      this.passwordComplexitySetting = result.setting;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.staff) {
      this.user = changes.staff.currentValue;

      if (!this.user.biositeStaffDetails) {
        this.user.biositeStaffDetails = new BiositeStaffDataDto();
      }

      if (this.user.lineManager) {
        this.staffUserLookupTableModal.selected = this.user.lineManager.lineManagerDetails;
        this.getSelectedUser();
      }
    }
  }

  updateProjectAdministratorUserDetails(
    params: UpdateProjectAdministratorUserDetailsInput
  ): Observable<any> {
    return this._staffsAndUsersServiceProxy
      .updateProjectAdministratorUserDetails(params)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.notify.info(this.l('SavedSuccessfully'));
        })
      );
  }

  updateMyOwnProjectAdministratorUserDetails(
    params: UpdateProjectAdministratorUserDetailsInput
  ): Observable<any> {
    return this._staffsAndUsersServiceProxy
      .updateMyOwnProjectAdministratorUserDetails(params)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.notify.info(this.l('SavedSuccessfully'));
        })
      );
  }

  updateProjectSupervisorDetails(
    params: EditSupervisorUserDetailsInput
  ): Observable<any> {
    return this._staffsAndUsersServiceProxy
      .updateSupervisorUserDetails(params)
      .pipe(
        finalize(() => {
          this.saving = false;
        })
      );
  }

  updateMyOwnProjectSupervisorDetails(
    params: EditSupervisorUserDetailsInput
  ): Observable<any> {
    return this._staffsAndUsersServiceProxy
      .updateMyOwnSupervisorUserDetails(params)
      .pipe(
        finalize(() => {
          this.saving = false;
        })
      );
  }

  updateAdminUserDetails(params: EditAdminUserDetailsInput): Observable<any> {
    return this._staffsAndUsersServiceProxy.updateAdminUserDetails(params).pipe(
      finalize(() => {
        this.saving = false;
      })
    );
  }

  updateMyOwnAdminUserDetails(
    params: EditAdminUserDetailsInput
  ): Observable<any> {
    return this._staffsAndUsersServiceProxy
      .updateMyOwnAdminUserDetails(params)
      .pipe(
        finalize(() => {
          this.saving = false;
        })
      );
  }

  save(): void {
    this.saveInternal().subscribe(
      (x) => {
        this.notify.success(this.l('SavedSuccessfully'));
      },
      () => {}
    );
  }

  changeUserPassword(): void {
    const body = new ChangeUserPasswordInput();
    body.userId = this.user.userId;
    body.newPassword = this.newPassword;

    this._userService.changeUserPassword(body).subscribe(
      () => {
        this.notify.success(this.l('SuccessfullyChangeUserPassword'));
        this.newPassword = null;
      },
      () => this.notify.error(this.l('CannotChangeUserPassword'))
    );
  }

  sendEmailActivationLink(): void {
    const body = new SendEmailActivationLinkInput();
    body.emailAddress = this.user.contactDetails.emailAddress;

    this._accountServiceProxy.sendEmailActivationLink(body).subscribe(
      () => this.notify.success(this.l('SuccessfullySendActivationEmail')),
      () => this.notify.error(this.l('CannotSendActivationEmail'))
    );
  }

  openSelectUserModal() {
    this.staffUserLookupTableModal.subordinatedStaffId = this.user.staffId;
    this.staffUserLookupTableModal.ownDetailsEdit = this.ownDetailsEdit
      ? this.ownDetailsEdit
      : false;

    this.staffUserLookupTableModal.show();
  }

  setUserIdNull() {
    this.staffUserLookupTableModal.id = null;
    this.staffUserLookupTableModal.selected = null;
    this.signOffPerson = null;
  }

  getSelectedUser() {
    if (!this.staffUserLookupTableModal.selected) return;

    this.signOffPerson = `${this.staffUserLookupTableModal.selected.contactDetails.lastName} ${this.staffUserLookupTableModal.selected.contactDetails.firstName}`;
  }

  private saveInternal(): Observable<void> {
    this.saving = true;

    if (this._permissionChecker.isGranted('Pages.Role.ProjectAdministrator')) {
      let params = new UpdateProjectAdministratorUserDetailsInput({
        userId: this.user.userId,
        contactDetails: this.user.contactDetails,
        workDetails: this.user.workDetails,
        jobTitle: null,
        company: null,
        locations : []
      });

      return this.ownDetailsEdit
        ? this.updateMyOwnProjectAdministratorUserDetails(params)
        : this.updateProjectAdministratorUserDetails(params);
    } else if (this._permissionChecker.isGranted('Pages.Role.Supervisor')) {
      let params = new EditSupervisorUserDetailsInput({
        userId: this.user.userId,
        contactDetails: this.user.contactDetails,
        lineManagerStaffId:
          (this.staffUserLookupTableModal.selected &&
            this.staffUserLookupTableModal.selected.staffId) ||
          null,
          jobTitle: null,
          company: null,
          locations : []
      });

      return this.ownDetailsEdit
        ? this.updateMyOwnProjectSupervisorDetails(params)
        : this.updateProjectSupervisorDetails(params);
    } else {
      let params = new EditAdminUserDetailsInput({
        userId: this.user.userId,
        contactDetails: this.user.contactDetails,
        jobTitle: null,
        company: null,
        locations : []
      });

      return this.ownDetailsEdit
        ? this.updateMyOwnAdminUserDetails(params)
        : this.updateAdminUserDetails(params);
    }
  }
}
